import dayjs from 'dayjs'

import type { UserOut } from 'src/api/intranet'
import { RECENT_JOIN_DAYS } from 'src/features/people/constants'
import { FORMAT_DATE_MONTH_YEAR, FORMAT_MONTH_DAY_LONG } from 'src/utils/date'
import { getInitials as getSharedInitials } from 'src/utils/getInitials'
import { safeHttpUrl } from 'src/utils/url'
import { userDisplayName } from 'src/utils/user'

export type PersonForCard = {
  id?: number
  scimId?: string | null
  name?: string
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  department?: string | null
  position?: string | null
  phone?: string | null
  photo_url?: string | null
  linkedin_url?: string | null
  about_me?: string | null
  employment_date?: string | null
  isNew?: boolean
}

const DAY_MS = 24 * 60 * 60 * 1000

const isRecentlyJoined = (
  employmentDate: string | null | undefined
): boolean => {
  if (!employmentDate) return false
  const diff = Date.now() - new Date(employmentDate).getTime()

  return diff >= 0 && diff <= RECENT_JOIN_DAYS * DAY_MS
}

const splitName = (display: string | null | undefined): [string, string] => {
  const parts = (display ?? '').trim().split(/\s+/).filter(Boolean)

  if (parts.length === 0) return ['', '']
  const [first, ...rest] = parts

  return [first, rest.join(' ')]
}

export const userToPerson = (u: UserOut): PersonForCard => {
  const [fallbackFirst, fallbackLast] = splitName(u.display_name)
  const first_name = u.given_name ?? fallbackFirst
  const last_name = u.family_name ?? fallbackLast

  return {
    id: u.id,
    scimId: u.scim_id,
    name: userDisplayName(u, ''),
    first_name,
    last_name,
    email: u.primary_email,
    position: u.title ?? null,
    phone: u.phone ?? null,
    photo_url: u.photo_url ?? null,
    department:
      (u.departments ?? [])
        .map((d) => d.display ?? d.value)
        .filter(Boolean)
        .join(', ') || null,
    linkedin_url: u.profile?.linkedin_url ?? null,
    about_me: u.profile?.about_me ?? null,
    employment_date: u.profile?.employment_date ?? null,
    isNew: isRecentlyJoined(u.profile?.employment_date)
  }
}

const AVATAR_COLORS = ['#C2255C', '#1971C2', '#7048E8', '#087F5B', '#E67700']

export const getInitials = (firstName: string, lastName: string) =>
  getSharedInitials(firstName, lastName)

export const getAvatarColor = (name: string) => {
  let hash = 0

  for (let i = 0; i < name.length; i++) {
    hash = (name.codePointAt(i) ?? 0) + ((hash << 5) - hash)
  }

  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

export const getRoleName = (role: string) => {
  return role
    ?.split('_')
    ?.map((r) => r?.charAt(0)?.toUpperCase() + r?.slice(1))
    ?.join(' ')
}

const formatDate = (d?: string | null) =>
  d ? dayjs(d).format(FORMAT_DATE_MONTH_YEAR) : '-'

// Birthdays are stored month/day only (the backend pins the year to a sentinel),
// so render them without a year.
const formatMonthDay = (d?: string | null) =>
  d ? dayjs(d).format(FORMAT_MONTH_DAY_LONG) : '-'

/**
 * Derives the display strings shared by the profile and person-detail pages
 * from a single `UserOut`, so the two views never drift apart. `email` and
 * `linkedinUrl` stay nullable because the views render them as links.
 */
export const getPersonDisplayFields = (user: UserOut) => ({
  name: userDisplayName(user, '-'),
  jobTitle: user.title ?? '-',
  department:
    (user.departments ?? [])
      .map((d) => d.display ?? d.value)
      .filter(Boolean)
      .join(', ') || '-',
  managerScimId: user.manager?.scim_id ?? null,
  managerName: user.manager?.display_name ?? user.manager?.scim_id ?? '-',
  phone: user.phone ?? '-',
  email: user.primary_email ?? null,
  aboutMe: user.profile?.about_me ?? '',
  linkedinUrl: user.profile?.linkedin_url ?? null,
  safeLinkedin: safeHttpUrl(user.profile?.linkedin_url ?? null),
  employmentDate: formatDate(user.profile?.employment_date),
  birthdate: formatMonthDay(user.profile?.birthdate)
})

export type PersonDisplayFields = ReturnType<typeof getPersonDisplayFields>
